import React, { Component } from 'react';
import { ApolloProvider } from 'react-apollo';

import CUILayout from 'crehana-ui/Layout';
import RootErrorBoundaryWithSentry from 'Jsx/Shared/ErrorBoundaries/RootErrorBoundaryWithSentry';
import ProductsContainer from 'Jsx/ContextApis/Products';
import { AuthProvider } from 'Jsx/ContextApis/AuthContext';

import Channels from 'Jsx/Analytics/Channels';
import FacebookPixelInit from 'Jsx/Analytics/FacebookPixelInit';

import { ColorTheme } from 'Jsx/global.d.ts';

import createApolloClient from './ApolloClient';
import { I18nProvider } from './i18n/I18nProvider';
import InitI18n from './i18n/InitI18n';
import GlobalStyles from './GlobalStyles';
import UIThemeProvider from './UITheme/UIThemeProvider';

import { CookiesDisabledWarning } from './components';

export interface LayoutProps {
  /** Optional custom apollo client */
  client?: any;
  /**
   * If initialI18nStore AND initialLanguage are provided, I18nProvider wrapper
   * will be included.
   */
  initialI18nStore?: any;
  /**
   * If initialI18nStore AND initialLanguage are provided, I18nProvider wrapper
   * will be included.
   */
  initialLanguage?: string;
  /**
   * crehana-ui Layout wrapper
   *
   * @default true
   */
  withCUI?: boolean;
  /**
   * Products context provider wrapper
   *
   * @default true
   */
  withSC?: boolean;
  /**
   * Enables styled components GlobalStyles to use the custom theme
   * from initial state
   */
  colorTheme?: ColorTheme;
  /**
   * Includes the UITheme provider (needed to handle light/dark mode).
   *
   * @default false
   */
  withUITheme?: boolean;
  /**
   * Controls if you want to send data to channels service
   */
  sendAnalyticsToChannels?: Boolean;
  /**
   * Controls if you want to send window.fbq('init', {})
   */
  sendFacebookPixelInit?: Boolean;
  /**
   * You should provide a single child to the Layout.
   */
  children: React.ReactChild;
}

class Layout extends Component<LayoutProps, {}> {
  validateFacebooKPixelInit = () => {
    const { sendFacebookPixelInit = true, children } = this.props;

    return sendFacebookPixelInit ? (
      <FacebookPixelInit>{children}</FacebookPixelInit>
    ) : (
      children
    );
  };

  validateAnalyticsChannels = () => {
    const { sendAnalyticsToChannels = true } = this.props;

    return sendAnalyticsToChannels ? (
      <Channels>{this.validateFacebooKPixelInit()}</Channels>
    ) : (
      this.validateFacebooKPixelInit()
    );
  };

  validateSC = () => {
    const { withSC = true, withUITheme } = this.props;

    return withSC ? (
      <ProductsContainer darkModeSupported={withUITheme}>
        {this.validateAnalyticsChannels()}
      </ProductsContainer>
    ) : (
      this.validateAnalyticsChannels()
    );
  };

  validateCUI = () => {
    const { withCUI = true } = this.props;

    return withCUI ? (
      <CUILayout>{this.validateSC()}</CUILayout>
    ) : (
      this.validateSC()
    );
  };

  validateI18N = () => {
    const { initialI18nStore, initialLanguage } = this.props;

    if (initialI18nStore && initialLanguage) {
      return (
        <I18nProvider>
          <InitI18n
            initialI18nStore={initialI18nStore}
            initialLanguage={initialLanguage}
          >
            {this.validateCUI()}
          </InitI18n>
        </I18nProvider>
      );
    }

    return initialI18nStore && initialLanguage ? (
      <I18nProvider>{this.validateCUI()}</I18nProvider>
    ) : (
      this.validateCUI()
    );
  };

  render() {
    const { client, colorTheme, withUITheme } = this.props;

    return (
      <React.Fragment>
        <CookiesDisabledWarning />
        <RootErrorBoundaryWithSentry>
          <ApolloProvider client={client || createApolloClient()}>
            <AuthProvider>
              {colorTheme ? <GlobalStyles colorTheme={colorTheme} /> : null}
              {withUITheme ? (
                <UIThemeProvider>{this.validateI18N()}</UIThemeProvider>
              ) : (
                this.validateI18N()
              )}
            </AuthProvider>
          </ApolloProvider>
        </RootErrorBoundaryWithSentry>
      </React.Fragment>
    );
  }
}

export default Layout;
